import React, { useState, useEffect, useCallback } from "react";
import Cookies, { CookieAttributes } from "js-cookie";
import { Button } from "antd";
import { gsT } from "../i18n";
import styles from "./cookieConsent.module.less";
import { getOverseasRegion } from "src/gs-utils/core/config/domain";

export interface CookieConsentProps {
  /** 自定义样式类名 */
  className?: string;
  /** Cookie同意后的回调 */
  onAccept?: () => void;
  /** Cookie拒绝后的回调 */
  onDecline?: () => void;
  /** 自定义存储键名 */
  storageKey?: string;
  /** 自动隐藏延迟（毫秒），默认不自动隐藏 */
  autoHideDelay?: number;
  /** 横幅位置，默认bottom */
  position?: "top" | "bottom";
  /** 是否显示拒绝按钮，默认为true */
  showDeclineButton?: boolean;
}

const DEFAULT_STORAGE_KEY = "gs_cookie_consent";
const COOKIE_EXPIRES_DAYS = 365;
const COOKIE_GAUSIUM_DOMAIN = ".gausium-robot.com";
const COOKIE_GS_DOMAIN = ".gs-robot.com";

// 域名后缀到 Cookie 域名的映射
const DOMAIN_SUFFIX_MAP = new Map<string, string>([
  [COOKIE_GAUSIUM_DOMAIN, COOKIE_GAUSIUM_DOMAIN],
  [COOKIE_GS_DOMAIN, COOKIE_GS_DOMAIN],
]);

/**
 * @description Cookie同意横幅组件，负责在海外环境提示用户接受或拒绝Cookie write by cursor
 * @author Cursor
 * @date 2025-12-23
 */
export const CookieConsent: React.FC<CookieConsentProps> = ({
  className = "",
  onAccept,
  onDecline,
  storageKey = DEFAULT_STORAGE_KEY,
  autoHideDelay,
  position = "bottom",
  showDeclineButton = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // 只在海外地区（有REACT_APP_REGION环境变量）显示Cookie同意框
  // 国内不需要显示
  const overseasRegion = getOverseasRegion();
  const region = process.env.REACT_APP_REGION || '';
  const shouldShowCookieConsent = !!region || overseasRegion !== 'CN';
  const hostname = window.location.hostname;
  const cookieDomain =
    Array.from(DOMAIN_SUFFIX_MAP.entries()).find(([suffix]) => hostname.endsWith(suffix))?.[1] ?? '';
  // 从Cookie读取用户的同意结果，避免跨页面重复弹窗 write by cursor
  const readConsentFromCookie = useCallback(() => {
    try {
      return Cookies.get(storageKey);
    } catch (error) {
      // fallback to undefined to继续展示横幅
      console.error("[CookieConsent] Failed to read cookie consent", error);
      return undefined;
    }
  }, [storageKey]);

  // 将用户选择持久化到Cookie，附带时间戳便于追踪 write by cursor
  const persistConsentToCookie = useCallback(
    (value: "accepted" | "declined") => {
      try {
        const expiresAt = new Date();
        expiresAt.setFullYear(expiresAt.getFullYear() + 1); // 设置有效期为1年 write by cursor
        Cookies.set(storageKey, value, {
          ...{
            expires: COOKIE_EXPIRES_DAYS,
            path: "/",
            domain: cookieDomain,
          },
          expires: expiresAt,
        });
      } catch (error) {
        console.error("[CookieConsent] Failed to write cookie consent", error);
      }
    },
    [storageKey]
  );

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300); // 与CSS动画时间一致
  }, []);

  useEffect(() => {
    // 如果不需要显示Cookie同意框（国内），直接返回
    if (!shouldShowCookieConsent) {
      return;
    }

    // 检查用户是否已经做出选择
    const consent = readConsentFromCookie();
    if (!consent) {
      // 短暂延迟后显示，让页面先加载
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }

    // 如果设置了自动隐藏延迟
    if (autoHideDelay && consent === "accepted") {
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }
  }, [autoHideDelay, shouldShowCookieConsent, readConsentFromCookie, handleClose]);

  const handleAccept = () => {
    persistConsentToCookie("accepted");
    handleClose();
    onAccept?.();
  };

  const handleDecline = () => {
    persistConsentToCookie("declined");
    handleClose();
    onDecline?.();
  };

  // 国内不显示Cookie同意框
  if (!shouldShowCookieConsent) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={`${styles.cookieConsent} ${styles[position]} ${
        isClosing ? styles.closing : ""
      } ${className}`}
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.icon}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                fill="currentColor"
              />
              <circle cx="8" cy="10" r="1.5" fill="currentColor" />
              <circle cx="12" cy="8" r="1.5" fill="currentColor" />
              <circle cx="16" cy="10" r="1.5" fill="currentColor" />
              <circle cx="10" cy="14" r="1.5" fill="currentColor" />
              <circle cx="14" cy="14" r="1.5" fill="currentColor" />
            </svg>
          </div>
          <div className={styles.textContent}>
            <div className={styles.title}>{gsT("cookie_consent_title")}</div>
            <div className={styles.description}>
              {gsT("cookie_consent_description")}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          {showDeclineButton && (
            <Button
              className={styles.declineButton}
              onClick={handleDecline}
              size="large"
            >
              {gsT("cookie_consent_decline")}
            </Button>
          )}
          <Button
            type="primary"
            className={styles.acceptButton}
            onClick={handleAccept}
            size="large"
          >
            {gsT("cookie_consent_accept")}
          </Button>
        </div>
      </div>
    </div>
  );
};

